/**
 * Mi Boleta — useWaitlist · lista de espera de una localidad agotada (SDD-029).
 *
 * Junta las tres rutas cara-al-fan de `api-taquilla` (`/v1/waitlist/*`) en una
 * superficie que el CTA consume sin saber de HTTP:
 *   - una query que lee la posición del fan en la fila (`GET /status`),
 *   - `join()` para anotarse (`POST /enroll`),
 *   - `leave()` para darse de baja (`DELETE /cancel`).
 *
 * La posición **la calcula el backend** al momento; acá sólo se cachea el último
 * valor conocido. El **409 «ya estabas anotado»** NO es un fallo: trae la
 * posición en `meta.position`, así que se trata como un alta ya hecha.
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api';
import {
  cancelWaitlist,
  enrollWaitlist,
  getWaitlistStatus,
  type WaitlistStatusDto,
} from '@/lib/api/taquilla';

export interface UseWaitlistOptions {
  eventId: string;
  /** NULL/omitido = la fila de «cualquier localidad del evento». */
  localityId?: string | null;
  /** Sólo consulta la fila cuando está activo (fan con sesión + localidad agotada). */
  enabled?: boolean;
}

export interface WaitlistState {
  /** `true` si el fan tiene una fila viva en esta localidad. */
  enrolled: boolean;
  /** Posición 1-based, o `null` si no está anotado. */
  position: number | null;
  /** Primera lectura de la fila en curso. */
  isLoading: boolean;
  /** Falló leer el estado de la fila. */
  error: ApiError | null;
  /** Anotarse. Idempotente: si ya estaba, resuelve con su posición. */
  join: () => Promise<void>;
  /** Darse de baja. Los de atrás avanzan solos. */
  leave: () => Promise<void>;
  /** Una de las dos acciones (anotarse/baja) está en vuelo. */
  isMutating: boolean;
  /** Falló la última acción (anotarse/baja); `null` si salió bien. */
  actionError: ApiError | null;
}

function waitlistKey(eventId: string, localityId?: string | null): (string | null)[] {
  return ['waitlist', eventId, localityId ?? null];
}

function asApiError(err: unknown): ApiError | null {
  return err instanceof ApiError ? err : null;
}

export function useWaitlist({
  eventId,
  localityId = null,
  enabled = true,
}: UseWaitlistOptions): WaitlistState {
  const qc = useQueryClient();
  const key = waitlistKey(eventId, localityId);

  const statusQuery = useQuery<WaitlistStatusDto, ApiError>({
    queryKey: key,
    queryFn: () => getWaitlistStatus(eventId, localityId),
    enabled: enabled && !!eventId,
    staleTime: 0,
    retry: (failureCount, error) => {
      const status = error?.status;
      // 401/403/404 no se reintentan: no es un fallo transitorio.
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });

  const enrollMut = useMutation<WaitlistStatusDto, ApiError, void>({
    mutationFn: async () => {
      try {
        const res = await enrollWaitlist(eventId, localityId);
        return { enrolled: true, position: res.position };
      } catch (err) {
        // 409 «ya estabas anotado» no es un fallo: la posición viene en meta.
        if (err instanceof ApiError && err.status === 409) {
          const pos = err.meta?.position;
          return {
            enrolled: true,
            position: typeof pos === 'number' ? pos : null,
          };
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      qc.setQueryData<WaitlistStatusDto>(key, data);
      // Si el 409 no trajo posición, la refrescamos de la fuente.
      if (data.position === null) void qc.invalidateQueries({ queryKey: key });
    },
  });

  const cancelMut = useMutation<void, ApiError, void>({
    mutationFn: async () => {
      await cancelWaitlist(eventId, localityId);
    },
    onSuccess: () => {
      qc.setQueryData<WaitlistStatusDto>(key, { enrolled: false, position: null });
    },
  });

  const join = useCallback(async () => {
    await enrollMut.mutateAsync();
  }, [enrollMut]);

  const leave = useCallback(async () => {
    await cancelMut.mutateAsync();
  }, [cancelMut]);

  return {
    enrolled: statusQuery.data?.enrolled ?? false,
    position: statusQuery.data?.position ?? null,
    isLoading: statusQuery.isLoading,
    error: statusQuery.error ?? null,
    join,
    leave,
    isMutating: enrollMut.isPending || cancelMut.isPending,
    actionError: asApiError(enrollMut.error) ?? asApiError(cancelMut.error),
  };
}
